import { useCallback, useEffect, useState } from 'react';

import { ConfirmationDialog } from '../../components/confirmationDialog/ConfirmationDialog';
import { useDialogDispatch } from '../useDialog/useDialog';
import { ConfirmationTitle } from './Confirmation.title';
import { CancelFnc, ConfirmFnc, CustomState, UseConfirmDialogParams } from './ConfirmationDialog.types';

export const useConfirmationDialog = ({
  title,
  titleType,
  message,
  confirmButtonText,
  confirmationButtonType,
  confirmationButtonColor,
  hideConfirmButton,
  cancelButtonText,
  cancelButtonType,
  cancelButtonColor,
  hideCancelButton,
  hideDialogCloseButton,
  byQueue,
  maxWidth,
}: UseConfirmDialogParams) => {
  const setDialog = useDialogDispatch();

  const [isLoading, setLoading] = useState<boolean>(false);
  const [isConfirmationDialogOpen, setIsConfirmationDialogOpen] = useState<boolean | null>(null);
  const [customState, setCustomState] = useState<CustomState | null>(null);
  const [onConfirm, setConfirmFnc] = useState<ConfirmFnc | null>(null);
  const [onCancel, setCancelFnc] = useState<CancelFnc>();

  const handleOpenConfirmation = useCallback((confirmFnc: ConfirmFnc, cancelFnc?: CancelFnc, custom?: CustomState) => {
    setCustomState(custom ?? null);

    setConfirmFnc(() => confirmFnc);
    setCancelFnc(() => cancelFnc);

    setIsConfirmationDialogOpen(true);
  }, []);

  const handleClose = useCallback(
    (type: 'cancel' | 'closeDialog' = 'cancel') => {
      setConfirmFnc(null);
      setIsConfirmationDialogOpen(false);

      setDialog({ open: false, variant: 'custom' });

      if (type !== 'closeDialog' && onCancel) {
        onCancel();
      }
    },
    [onCancel, setDialog],
  );

  useEffect(() => {
    if (isConfirmationDialogOpen) {
      setDialog({
        open: true,
        variant: 'basic',
        title: <ConfirmationTitle title={customState?.title || title} titleType={titleType} />,
        dialogContent: (
          <ConfirmationDialog
            message={customState?.message || message}
            confirmButtonText={customState?.confirm || confirmButtonText}
            confirmationButtonType={confirmationButtonType}
            confirmationButtonColor={confirmationButtonColor}
            hideConfirmButton={hideConfirmButton}
            cancelButtonText={customState?.cancel || cancelButtonText}
            cancelButtonType={cancelButtonType}
            cancelButtonColor={cancelButtonColor}
            hideCancelButton={hideCancelButton}
            maxWidth={maxWidth}
            isLoading={isLoading}
            onConfirm={onConfirm as ConfirmFnc}
            onClose={handleClose}
          />
        ),
        disableBackdropClick: true,
        hideCloseButton: hideDialogCloseButton,
        byQueue,
        onClose: handleClose,
      });
    } else if (isConfirmationDialogOpen === false) {
      handleClose('closeDialog');
      setIsConfirmationDialogOpen(null);
    }
  }, [
    title,
    titleType,
    message,
    confirmButtonText,
    confirmationButtonType,
    confirmationButtonColor,
    hideConfirmButton,
    cancelButtonText,
    cancelButtonType,
    cancelButtonColor,
    hideCancelButton,
    maxWidth,
    hideDialogCloseButton,
    byQueue,
    isConfirmationDialogOpen,
    customState?.title,
    customState?.message,
    customState?.confirm,
    customState?.cancel,
    isLoading,
    onConfirm,
    handleClose,
    setDialog,
  ]);

  return {
    isOpen: isConfirmationDialogOpen,
    closeConfirmation: handleClose,
    openConfirmation: handleOpenConfirmation,
    setLoader: setLoading,
    isLoading,
  };
};
